export type StarburstSimpleParams = {
  pixelSnap?: boolean;
  chromaticAberration?: number;
  vignette?: number;
  paletteMode?: boolean;
  ditherEnabled?: boolean;
};

export class StarburstSimple {
  private gl: WebGL2RenderingContext;
  private vao: WebGLVertexArrayObject;
  private vbo: WebGLBuffer;
  private prog: WebGLProgram;
  private w = 0;
  private h = 0;

  constructor(gl: WebGL2RenderingContext, width: number, height: number) {
    this.gl = gl;
    const quad = new Float32Array([-1, -1, 0, 0, 3, -1, 2, 0, -1, 3, 0, 2]);
    this.vao = gl.createVertexArray()!;
    this.vbo = gl.createBuffer()!;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, quad, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 16, 8);
    gl.bindVertexArray(null);

    this.prog = this.makeProgram(VS, FS);
    this.resize(width, height);
  }

  resize(w: number, h: number) {
    this.w = Math.max(1, w);
    this.h = Math.max(1, h);
  }

  run(
    srcTex: WebGLTexture,
    _timeSeconds: number,
    p: StarburstSimpleParams = {},
  ) {
    const gl = this.gl;
    const u = {
      pixelSnap: p.pixelSnap ? 1 : 0,
      chromaticAberration: p.chromaticAberration ?? 0,
      vignette: p.vignette ?? 0,
      paletteMode: p.paletteMode ? 1 : 0,
      ditherEnabled: p.ditherEnabled ? 1 : 0,
    };

    gl.useProgram(this.prog);
    gl.bindVertexArray(this.vao);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, srcTex);
    gl.uniform1i(gl.getUniformLocation(this.prog, "uSrc"), 0);
    gl.uniform2f(
      gl.getUniformLocation(this.prog, "uResolution"),
      this.w,
      this.h,
    );
    gl.uniform1i(gl.getUniformLocation(this.prog, "uPixelSnap"), u.pixelSnap);
    gl.uniform1f(
      gl.getUniformLocation(this.prog, "uChromaticAberration"),
      u.chromaticAberration,
    );
    gl.uniform1f(gl.getUniformLocation(this.prog, "uVignette"), u.vignette);
    gl.uniform1i(
      gl.getUniformLocation(this.prog, "uPaletteMode"),
      u.paletteMode,
    );
    gl.uniform1i(
      gl.getUniformLocation(this.prog, "uDitherEnabled"),
      u.ditherEnabled,
    );

    gl.drawArrays(gl.TRIANGLES, 0, 3);

    gl.bindVertexArray(null);
    gl.useProgram(null);
  }

  dispose() {
    const gl = this.gl;
    gl.deleteProgram(this.prog);
    gl.deleteBuffer(this.vbo);
    gl.deleteVertexArray(this.vao);
  }

  private makeProgram(vsSrc: string, fsSrc: string) {
    const gl = this.gl;
    const vs = gl.createShader(gl.VERTEX_SHADER)!;
    gl.shaderSource(vs, vsSrc);
    gl.compileShader(vs);
    if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS))
      throw new Error(gl.getShaderInfoLog(vs) || "VS error");

    const fs = gl.createShader(gl.FRAGMENT_SHADER)!;
    gl.shaderSource(fs, fsSrc);
    gl.compileShader(fs);
    if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS))
      throw new Error(gl.getShaderInfoLog(fs) || "FS error");

    const p = gl.createProgram()!;
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.bindAttribLocation(p, 0, "aPos");
    gl.bindAttribLocation(p, 1, "aUv");
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS))
      throw new Error(gl.getProgramInfoLog(p) || "Link error");
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    return p;
  }
}

const VS = `#version 300 es
layout(location=0) in vec2 aPos;
layout(location=1) in vec2 aUv;
out vec2 vUv;
void main(){ vUv = aUv; gl_Position = vec4(aPos,0.0,1.0); }
`;

const FS = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 oColor;

uniform sampler2D uSrc;
uniform vec2  uResolution;
uniform int   uPixelSnap;
uniform float uChromaticAberration;
uniform float uVignette;
uniform int   uPaletteMode;
uniform int   uDitherEnabled;

vec2 snap(vec2 uv){
  vec2 texel = 1.0 / uResolution;
  vec2 q = floor(uv / texel + 0.5) * texel;
  return (uPixelSnap == 1) ? q : uv;
}

float bayer4(vec2 p){
  int x = int(mod(p.x, 4.0));
  int y = int(mod(p.y, 4.0));
  int m[16] = int[16](0,8,2,10, 12,4,14,6, 3,11,1,9, 15,7,13,5);
  return (float(m[y*4+x]) + 0.5) / 16.0;
}

const int PAL_SIZE = 8;
const vec3 PAL[PAL_SIZE] = vec3[PAL_SIZE](
  vec3(1.0,1.0,1.0),
  vec3(0.455,1.0,0.353),
  vec3(0.0,0.898,1.0),
  vec3(1.0,0.831,0.0),
  vec3(0.77,0.0,1.0),
  vec3(0.78,0.78,0.78),
  vec3(0.42,0.42,0.42),
  vec3(0.15,0.15,0.15)
);

vec3 quantize(vec3 c){
  float best = 1e9; vec3 bestC = c;
  vec3 w = vec3(0.2126,0.7152,0.0722);
  for(int i=0;i<PAL_SIZE;i++){
    vec3 p = PAL[i];
    float e = dot((c-p)*(c-p), w);
    if(e < best){ best = e; bestC = p; }
  }
  return bestC;
}

void main(){
  vec3 outc = texture(uSrc, snap(vUv)).rgb;

  if(uChromaticAberration > 0.0){
    vec2 texel = 1.0 / uResolution;
    float ca  = uChromaticAberration * 0.33 * texel.x;
    float ca2 = ca * 0.6;
    float r = texture(uSrc, snap(vUv + vec2( ca, 0.0))).r;
    float g = texture(uSrc, snap(vUv + vec2(0.0, 0.0))).g;
    float b = texture(uSrc, snap(vUv + vec2(-ca2,0.0))).b;
    outc = vec3(r,g,b);
  }

  if(uVignette > 0.0){
    vec2 center = vUv - 0.5;
    float dist = length(center);
    float vig = smoothstep(0.9, 0.2, dist);
    vig = pow(vig, 0.8);
    outc *= mix(1.0, vig, uVignette);
  }
  if(uPaletteMode == 1){
    if(uDitherEnabled == 1){
      float l = dot(outc, vec3(0.2126,0.7152,0.0722));
      float t = bayer4(vUv * uResolution);
      float lq = floor(l * 7.0 + t) / 7.0;          // 8 luma bands
      vec3 approx = normalize(outc + 1e-5) * lq;    // keep hue direction
      outc = quantize(approx);
    } else {
      outc = quantize(outc);
    }
  }

  oColor = vec4(clamp(outc, 0.0, 1.0), 1.0);
}
`;
